// src/pages/Employees.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Tabs, Tab, Stack, Pagination, Grid, Divider,
  MenuItem, FormControlLabel, Checkbox, IconButton, InputAdornment
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BadgeIcon from "@mui/icons-material/Badge";
import { ref, onValue, get, update, set } from "firebase/database";
import { db, app, functions as fbFunctions } from "../firebase";
import { httpsCallable } from "firebase/functions";

/* ----------------------- helpers ----------------------- */
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = new Date(Number(ts));
  return isNaN(d) ? "—" : d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
};
const fmtBirthdayFromDDMM = (ddmm) => {
  if (!ddmm || String(ddmm).length !== 4) return "—";
  const dd = ddmm.slice(0, 2), mm = Number(ddmm.slice(2, 4)) - 1;
  const d = new Date(2000, mm, Number(dd));
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
const safe = (o, path, fb = "—") => {
  try {
    return path.split(".").reduce((a, k) => (a && a[k] != null ? a[k] : undefined), o) ?? fb;
  } catch { return fb; }
};
const joinAddr = (a) =>
  a ? [a.street, a.area, a.town, a.city, a.state, a.country, a.postalCode].filter(Boolean).join(", ") : "—";

/* --- create user in Auth WITHOUT switching current admin session --- */
async function createEmployeeAuth(email, password) {
  const apiKey = app?.options?.apiKey;
  if (!apiKey) throw new Error("Missing Firebase apiKey");
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password, returnSecureToken: false }) }
  );
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || "Failed to create auth user");
  const data = await res.json();
  return data.localId;
}

/* ----------------------- ADDED: password + mail helpers ----------------------- */
const EMP_PORTAL_URL = "https://thinkersclub.online";

// strong-ish 12–14 char password (letters, digits, symbols)
function generatePassword(len = 12) {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()_+-=";
  const array = new Uint32Array(len);
  window.crypto.getRandomValues(array);
  return Array.from(array, (x) => alphabet[x % alphabet.length]).join("");
}

function wrapEmailHtml(subject, innerHtml) {
  return `<!doctype html><html><head><meta charSet="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><title>${subject}</title></head>
  <body style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;background:#f5f7fb;margin:0;padding:24px;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:640px;margin:0 auto;background:#ffffff;border-radius:12px;box-shadow:0 6px 18px rgba(15,23,42,.06);">
      <tr><td style="padding:24px 24px 8px;">
        <div style="font-weight:800;font-size:20px;color:#0f172a;">Thinkers Club</div>
        <div style="color:#64748b;font-size:13px;margin-top:4px;">HR Onboarding</div>
      </td></tr>
      <tr><td style="padding:8px 24px 24px;line-height:1.6;color:#0f172a;">${innerHtml}</td></tr>
      <tr><td style="padding:16px 24px;color:#64748b;border-top:1px solid #eef2f7;font-size:12px;">
        Sent by HR • Thinkers Club<br/>This mailbox is not monitored for support. For help, contact <a href="mailto:support@thinkersclub.online">support@thinkersclub.online</a>.
      </td></tr>
    </table>
  </body></html>`;
}

const SEND_URL = import.meta.env.VITE_SENDMAIL_URL;

async function sendWelcomeEmail({ to, name, tempPassword }) {
  const subject = "Welcome to Thinkers Club";
  const html = wrapEmailHtml(
    subject,
    `
      <h2 style="margin:0 0 12px 0;">Welcome aboard, ${name || "there"} 🎉</h2>
      <p>Hi ${name || "there"},</p>
      <p>Your employee account has been created.</p>
      <ul>
        <li><b>Login email:</b> ${to}</li>
        <li><b>Temporary password:</b> ${tempPassword}</li>
      </ul>
      <p>Start here: <a href="${EMP_PORTAL_URL}">${EMP_PORTAL_URL}</a></p>
      <p>For security, please sign in and change your password immediately.</p>
      <p>— HR Team</p>
    `.trim()
  );

  try {
    const call = httpsCallable(fbFunctions, "sendMailCallable");
    const resp = await call({ to, subject, html });
    if (resp?.data?.ok === false) {
      throw new Error(resp.data.error || "sendMailCallable failed");
    }
    return;
  } catch (err) {
    console.warn("sendMailCallable failed:", err);
  }

  if (!SEND_URL) throw new Error("VITE_SENDMAIL_URL missing (and callable failed)");
  try {
    const auth = await import("firebase/auth");
    const getAuth = auth.getAuth;
    const user = getAuth()?.currentUser;
    const idToken = user ? await user.getIdToken(/* forceRefresh */ true) : null;

    const r = await fetch(SEND_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ to, subject, html }),
    });

    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      throw new Error(`sendMail HTTP ${r.status}: ${txt || r.statusText}`);
    }
    return;
  } catch (err) {
    console.error("Fallback sendMail failed:", err);
    throw err;
  }
}
/* -------------------------------------------------------------------- */

/* ---------- tiny UI atoms ---------- */
const L = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1, minWidth: 120 }}>
    {children}
  </Typography>
);
const V = ({ children }) => (
  <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
    {children || "—"}
  </Typography>
);
function Row({ label, value }) {
  return (
    <Grid container columnSpacing={2} sx={{ mb: 1.5 }}>
      <Grid item xs={5} sm={4} md={3}><L>{label}</L></Grid>
      <Grid item xs={7} sm={8} md={9}><V>{value}</V></Grid>
    </Grid>
  );
}
function Section({ title, children }) {
  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}
const Tag = ({ children, color = "primary" }) => (
  <Chip label={children} color={color} size="small" variant="outlined" sx={{ mr: 1, mb: 1 }} />
);

/* =============================== Component =============================== */
export default function Employees() {
  const [employees, setEmployees] = useState({});
  const [users, setUsers] = useState({});
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [cEmail, setCEmail] = useState("");
  const [cName, setCName] = useState("");
  const [cRole] = useState("EMPLOYEE");
  const [cBusy, setCBusy] = useState(false);
  const [cMsg, setCMsg] = useState("");
  const [createdUid, setCreatedUid] = useState("");
  const [generatedPwd, setGeneratedPwd] = useState("");

  // NEW: reopen steps dialog state
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenStep, setReopenStep] = useState("step5");

  // ID Card
  const [idCardOpen, setIdCardOpen] = useState(false);
  const [idTarget, setIdTarget] = useState(null);
  const [idBusy, setIdBusy] = useState(false);
  const [idForm, setIdForm] = useState({
    employeeId: "",
    companyName: "",
    companySite: "",
    generated: false,
  });

  const ITEMS_PER_PAGE = 15;

  /* -------- load data -------- */
  useEffect(() => {
    const unsub = onValue(ref(db, "/employees"), (snap) => setEmployees(snap.val() || {}));
    return () => unsub();
  }, []);
  useEffect(() => { get(ref(db, "/users")).then((s) => setUsers(s.val() || {})); }, []);

  /* -------- prepare table list -------- */
  const list = useMemo(() => {
    return Object.entries(employees).map(([uid, e]) => {
      const pf = e?.submittedProfile?.profileFields || {};
      const emp = e?.submittedProfile?.employment || {};
      const email = e.email || users?.[uid]?.email || pf?.email || "—";
      return {
        uid,
        name: e.fullName || pf.fullName || "—",
        email,
        portrait: e?.submittedProfile?.portrait || "",
        department: emp.department || "—",
        designation: emp.designation || "—",
        onboardingStatus: e.onboardingStatus || "—",
        onboardingCompleted: e.onboardingCompleted ?? false,
        active: e.active ?? true,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
        idCardGenerated: !!e?.idCardGenerated,
        idCard: e?.idCard || null,
      };
    });
  }, [employees, users]);

  /* -------- categorize -------- */
  const invited = list.filter(e => e.onboardingStatus === "INVITED" && e.active);
  const current = list.filter(e => e.active && ["IN_PROGRESS", "COMPLETED"].includes(e.onboardingStatus));
  const incomplete = list.filter(e => e.active && e.onboardingStatus === "IN_PROGRESS" && !e.onboardingCompleted);
  const offboarded = list.filter(e => !e.active || e.onboardingStatus === "OFFBOARDED");
  const tabs = [
    { label: "Invited", data: invited },
    { label: "Current Employees", data: current },
    { label: "Incomplete Profiles", data: incomplete },
    { label: "Offboarded Employees", data: offboarded },
  ];

  /* -------- search + pagination -------- */
  const searched = tabs[tab].data.filter(
    (e) =>
      e.name.toLowerCase().includes(q.toLowerCase()) ||
      e.email.toLowerCase().includes(q.toLowerCase())
  );
  const totalPages = Math.ceil(searched.length / ITEMS_PER_PAGE);
  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const pagedData = searched.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  useEffect(() => { setPage(1); }, [tab, q]);

  /* -------- enable/disable -------- */
  const handleToggleEmployee = async (uid, makeActive) => {
    try {
      await update(ref(db, `/employees/${uid}`), {
        active: makeActive,
        onboardingStatus: makeActive ? "IN_PROGRESS" : "OFFBOARDED",
        updatedAt: Date.now(),
      });
      setMsg(makeActive ? "✅ Employee re-enabled." : "✅ Employee offboarded.");
    } catch {
      setMsg("❌ Failed to update status");
    } finally {
      setConfirmToggle(null);
    }
  };

  /* -------- create employee -------- */
  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setCMsg("");
    setGeneratedPwd("");
    const email = cEmail.trim().toLowerCase();
    const name = (cName || "").trim();
    if (!name) return setCMsg("Please provide full name.");
    if (!email) return setCMsg("Please provide email.");
    const pwd = generatePassword(12);
    setCBusy(true);
    try {
      const uid = await createEmployeeAuth(email, pwd);
      const now = Date.now();
      await Promise.all([
        set(ref(db, `/users/${uid}`), { email, role: "EMPLOYEE", createdAt: now, updatedAt: now }),
        set(ref(db, `/roles/${uid}`), { role: "EMPLOYEE", active: true }),
        set(ref(db, `/employees/${uid}`), {
          fullName: name,
          email,
          active: true,
          onboardingStatus: "INVITED",
          onboardingCompleted: false,
          progress: {},
          submittedProfile: { profileFields: { email, fullName: name } },
          createdAt: now,
          updatedAt: now,
        }),
      ]);

      try {
        await sendWelcomeEmail({ to: email, name, tempPassword: pwd });
      } catch (mailErr) {
        console.warn("sendMail failed:", mailErr);
      }

      setCMsg("✅ Employee created successfully. A welcome email has been sent.");
      setGeneratedPwd(pwd);
      setCreatedUid(uid);
      setCEmail("");
      setCName("");
    } catch (err) {
      const raw = String(err?.message || err);
      const friendly =
        raw.includes("EMAIL_EXISTS") ? "Email already exists."
          : raw.includes("INVALID_EMAIL") ? "Invalid email format."
          : "Failed to create employee.";
      setCMsg(`❌ ${friendly}`);
    } finally {
      setCBusy(false);
    }
  };

  /* -------- selection + derived -------- */
  const selectedFull = selected ? employees[selected.uid] : null;
  const pf = selectedFull?.submittedProfile?.profileFields || {};
  const emp = selectedFull?.submittedProfile?.employment || {};
  const portrait = selectedFull?.submittedProfile?.portrait || "";

  // *** CHANGED: support multiple emergency contacts (show only those that exist) ***
  const emergencies = Array.isArray(pf?.emergencyContacts)
    ? pf.emergencyContacts.filter(Boolean)
    : [];
  // ------------------------------------------------------------------------------

  const addr = selectedFull?.submittedProfile?.address || {};
  const skills = safe(selectedFull, "submittedProfile.background.skills", []);
  const certs = safe(selectedFull, "submittedProfile.background.certifications", []);
  const qualification = safe(selectedFull, "submittedProfile.background.highestQualification");
  const yearsExp = safe(selectedFull, "submittedProfile.background.yearsExperience");

  /* -------- reopen steps (new) -------- */
  const doReopenSteps = async () => {
    if (!selected) return;
    setReopenBusy(true);
    try {
      const steps = ["step1", "step2", "step3", "step4", "step5"];
      const idx = steps.indexOf(reopenStep);
      const reopenList = idx >= 0 ? steps.slice(idx) : [];

      const updates = {
        onboardingStatus: "IN_PROGRESS",
        onboardingCompleted: false,
        updatedAt: Date.now(),
      };
      reopenList.forEach((s) => { updates[`progress/${s}`] = false; });

      await update(ref(db, `/employees/${selected.uid}`), updates);
      setMsg(`🔓 Reopened ${reopenList.join(", ")} for editing.`);
      setReopenOpen(false);
    } catch (e) {
      setMsg("❌ Could not reopen steps.");
    } finally {
      setReopenBusy(false);
    }
  };

  // ID Card handlers
  const openIdCardFor = (row) => {
    setIdTarget(row);
    const full = employees[row.uid];
    const pfLocal = full?.submittedProfile?.profileFields || {};
    const empLocal = full?.submittedProfile?.employment || {};

    const existing = full?.idCard || {};
    const generated = !!full?.idCardGenerated;

    setIdForm({
      employeeId: existing.employeeId || "",
      companyName: existing.companyName || "",
      companySite: existing.companySite || "",
      generated,
      joiningDate: empLocal.dateOfJoin || empLocal.dateOfJoining || null,
      phone: pfLocal.phone || "",
      email: row.email || "",
      name: full?.fullName || pfLocal.fullName || row.name || "",
    });
    setIdCardOpen(true);
  };

  const saveIdCard = async () => {
    if (!idTarget) return;
    const { employeeId, companyName, companySite, generated } = idForm;
    if (!employeeId || !companyName || !companySite) {
      setMsg("❌ Please fill Employee ID, Company Name and Company Website.");
      return;
    }
    setIdBusy(true);
    try {
      await update(ref(db, `/employees/${idTarget.uid}`), {
        idCard: { employeeId, companyName, companySite },
        idCardGenerated: !!generated,
        updatedAt: Date.now(),
      });
      setMsg("✅ ID card details saved.");
      setIdCardOpen(false);
    } catch (e) {
      setMsg("❌ Failed to save ID card details.");
    } finally {
      setIdBusy(false);
    }
  };
  // ------------------------------------------------------

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Employees</Typography>
          <Typography variant="body2" color="text.secondary">Manage employees, onboarding status and ID cards</Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Create Employee
          </Button>
          <Button
            variant="outlined"
            startIcon={<ManageAccountsIcon />}
            onClick={() => { /* no-op (keeps same functionality) */ }}
            sx={{ textTransform: "none" }}
          >
            Admin
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        {tabs.map((t, i) => (
          <Tab key={i} label={`${t.label} (${t.data.length})`} />
        ))}
      </Tabs>

      {/* Search + Create */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            sx={{ minWidth: 320, flex: 1 }}
            InputProps={{
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ("")}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {pagedData.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">No employees found.</Typography>
            </Box>
          ) : (
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Name</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Email</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Department</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Designation</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map((e, idx) => (
                  <tr
                    key={e.uid}
                    style={{
                      background: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)",
                      transition: "background .12s",
                    }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = "rgba(15,23,42,0.03)"; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)"; }}
                  >
                    <td style={{ padding: "12px 16px", cursor: "pointer", verticalAlign: "middle" }} onClick={() => setSelected(e)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar src={e.portrait} sx={{ width: 34, height: 34, bgcolor: (e.portrait ? "transparent" : "primary.main") }}>
                          {!e.portrait && (e.name?.[0] || "?")}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.name}</Typography>
                          <Typography variant="caption" color="text.secondary">UID: {e.uid}</Typography>
                        </Box>
                      </Box>
                    </td>

                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <Typography variant="body2">{e.email}</Typography>
                    </td>

                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <Typography variant="body2">{e.department}</Typography>
                    </td>

                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <Typography variant="body2">{e.designation}</Typography>
                    </td>

                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <Chip
                        label={
                          !e.active
                            ? "Disabled"
                            : e.onboardingCompleted
                            ? "Completed"
                            : e.onboardingStatus
                        }
                        color={!e.active ? "error" : e.onboardingCompleted ? "success" : "warning"}
                        size="small"
                      />
                    </td>

                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button size="small" onClick={() => setSelected(e)} startIcon={<AssignmentIndIcon />}>View</Button>

                        <Chip
                          size="small"
                          variant="outlined"
                          color={e.idCardGenerated ? "success" : "default"}
                          label={e.idCardGenerated ? "ID Generated" : "ID Not Generated"}
                        />

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openIdCardFor(e)}
                          startIcon={<BadgeIcon />}
                        >
                          ID Card
                        </Button>
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 2, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* View / Toggle Dialog — Static, polished sections */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar src={portrait} sx={{ width: 40, height: 40 }}>
                  {(selected.name || "—")[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selected.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">UID: {selected.uid}</Typography>
                </Box>
                <Chip
                  label={
                    !selected.active
                      ? "DISABLED"
                      : selected.onboardingCompleted
                      ? "COMPLETED"
                      : selected.onboardingStatus
                  }
                  color={!selected.active ? "error" : selected.onboardingCompleted ? "success" : "warning"}
                  size="small"
                />
              </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
              {/* Basic Information */}
              <Section title="Basic Information">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Row label="Full Name" value={selected.fullName || pf.fullName || "—"} />
                    <Row label="Gender" value={pf.gender || "—"} />
                    <Row label="Phone" value={pf.phone || "—"} />
                    <Row label="Marital Status" value={pf.maritalStatus || "—"} />
                    <Row label="Blood Group" value={pf.bloodGroup || "—"} />
                    <Row label="Birthday 🎉" value={fmtBirthdayFromDDMM(pf.birthdayDDMM)} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Emergency Contacts</Typography>

                    {/* *** CHANGED: render multiple contacts if present; otherwise show — */}
                    {Array.isArray(emergencies) && emergencies.length > 0 ? (
                      emergencies.map((ec, i) => (
                        <Box key={i} sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Contact {i + 1}</Typography>
                          <Row label="Name" value={ec?.name || "—"} />
                          <Row label="Relation" value={ec?.relationship || ec?.relation || "—"} />
                          <Row label="Phone" value={ec?.phone || ec?.mobile || "—"} />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                    {/* ----------------------------------------------------------------- */}
                  </Grid>
                </Grid>
              </Section>

              {/* Employment */}
              <Section title="Employment Details">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Row label="Department" value={emp.department} />
                    <Row label="Designation" value={emp.designation} />
                    <Row label="Manager" value={emp.managerName} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Row label="Date of Joining" value={fmtDate(emp.dateOfJoin)} />
                    <Row label="Employment Type" value={emp.employeeType} />
                    <Row label="Shift" value={emp.workShift} />
                  </Grid>
                </Grid>
              </Section>

              {/* Background */}
              <Section title="Background & Experience">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Row label="Qualification" value={qualification} />
                    <Row label="Experience" value={typeof yearsExp === "number" ? `${yearsExp} years` : "—"} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <L>Skills</L>
                    <Box sx={{ mt: 0.5, mb: 1 }}>
                      {Array.isArray(skills) && skills.length > 0 ? skills.map((s) => <Tag key={s}>{s}</Tag>) : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </Box>
                    <L>Certifications</L>
                    <Box sx={{ mt: 0.5 }}>
                      {Array.isArray(certs) && certs.length > 0 ? certs.map((c) => <Tag key={c} color="success">{c}</Tag>) : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </Box>
                  </Grid>
                </Grid>
              </Section>

              {/* Addresses */}
              <Section title="Addresses">
                <Row label="Permanent Address" value={joinAddr(addr.permanent)} />
                <Row
                  label="Current Address"
                  value={
                    addr?.sameAsPermanent
                      ? `${joinAddr(addr.current)} (same as permanent)`
                      : joinAddr(addr.current)
                  }
                />
              </Section>
            </DialogContent>

            <DialogActions>
              {msg && (
                <Typography variant="caption" color={msg.startsWith("✅") ? "success.main" : "error.main"} sx={{ mr: 2 }}>
                  {msg}
                </Typography>
              )}
              <Button onClick={() => setSelected(null)}>Close</Button>

              <Button
                variant="contained"
                color="warning"
                onClick={() => setReopenOpen(true)}
                sx={{ mr: "auto", textTransform: "none" }}
              >
                Reopen Steps
              </Button>

              <Button
                variant="outlined"
                color={selected.active ? "error" : "success"}
                onClick={() => setConfirmToggle(true)}
                sx={{ textTransform: "none" }}
              >
                {selected.active ? "Disable Employee" : "Enable Employee"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm Toggle */}
      <Dialog open={!!confirmToggle} onClose={() => setConfirmToggle(null)}>
        <DialogTitle>
          {selected?.active ? "Offboard Employee" : "Re-enable Employee"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {selected?.active
              ? `You are about to offboard ${selected?.name}. They will lose portal access.`
              : `You are about to re-enable ${selected?.name}. They can access onboarding again.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmToggle(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={selected?.active ? "error" : "success"}
            onClick={() => handleToggleEmployee(selected.uid, !selected.active)}
          >
            {selected?.active ? "Yes, Disable" : "Yes, Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Employee</DialogTitle>
        <form onSubmit={handleCreateEmployee}>
          <DialogContent dividers>
            {cMsg && (
              <Alert severity={cMsg.startsWith("✅") ? "success" : "error"} sx={{ mb: 2 }}>
                {cMsg}
                {createdUid && <Box sx={{ mt: 1 }}>UID: {createdUid}</Box>}
                {generatedPwd && (
                  <Box sx={{ mt: 1 }}>
                    Temp Password (shown once): <code>{generatedPwd}</code>
                  </Box>
                )}
              </Alert>
            )}

            <TextField
              label="Full Name"
              type="text"
              fullWidth
              sx={{ mb: 2 }}
              value={cName}
              onChange={(e) => setCName(e.target.value)}
              required
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              sx={{ mb: 2 }}
              value={cEmail}
              onChange={(e) => setCEmail(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={cBusy}>
              {cBusy ? "Creating..." : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reopen Steps Dialog */}
      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)}>
        <DialogTitle>Reopen Onboarding Steps</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select from which step the employee can re-edit.
            <br />The selected step <b>and all following steps</b> will reopen.
          </Typography>
          <TextField
            label="Start from step"
            select
            fullWidth
            SelectProps={{ native: true }}
            value={reopenStep}
            onChange={(e) => setReopenStep(e.target.value)}
            sx={{ mb: 2 }}
          >
            <option value="step1">Step 1 — Basic Info</option>
            <option value="step2">Step 2 — Employment</option>
            <option value="step3">Step 3 — Background</option>
            <option value="step4">Step 4 — Image & Hobby</option>
            <option value="step5">Step 5 — Address</option>
          </TextField>
          <Typography variant="caption" color="text.secondary">
            This will mark onboarding as <b>IN PROGRESS</b> and the employee will be able to update the reopened steps.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={reopenBusy} onClick={doReopenSteps}>
            {reopenBusy ? "Reopening…" : "Reopen Steps"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ID CARD Dialog */}
      <Dialog open={idCardOpen} onClose={() => setIdCardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign / Edit ID Card</DialogTitle>
        <DialogContent dividers>
          {idTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Employee Details</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}><L>Name</L><V>{idForm.name || "—"}</V></Grid>
                <Grid item xs={12} sm={6}><L>Email</L><V>{idForm.email || "—"}</V></Grid>
                <Grid item xs={12} sm={6}><L>Phone</L><V>{idForm.phone || "—"}</V></Grid>
                <Grid item xs={12} sm={6}><L>Joining Date</L><V>{fmtDate(idForm.joiningDate)}</V></Grid>
              </Grid>
              <Divider sx={{ mt: 1.5, mb: 2 }} />
            </Box>
          )}

          <TextField
            label="Employee ID"
            value={idForm.employeeId}
            onChange={(e) => setIdForm({ ...idForm, employeeId: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Company Name"
            select
            fullWidth
            value={idForm.companyName}
            onChange={(e) => {
              const cv = e.target.value;
              setIdForm({
                ...idForm,
                companyName: cv,
                companySite:
                  cv === "THINK DIGITAL ONLINE" ? "www.thinkdigitalonline.in"
                  : cv === "TRIPLEW DIGITAL SOLUTIONS" ? "www.triplew.in"
                  : "",
              });
            }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="THINK DIGITAL ONLINE">THINK DIGITAL ONLINE</MenuItem>
            <MenuItem value="TRIPLEW DIGITAL SOLUTIONS">TRIPLEW DIGITAL SOLUTIONS</MenuItem>
          </TextField>

          <TextField
            label="Company Website"
            value={idForm.companySite}
            onChange={(e) => setIdForm({ ...idForm, companySite: e.target.value })}
            fullWidth
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={idForm.generated}
                onChange={(e) => setIdForm({ ...idForm, generated: e.target.checked })}
              />
            }
            label="Mark as generated (visible to employee)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdCardOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveIdCard} disabled={idBusy}>
            {idBusy ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}